require('./realtime-video.scss');

import * as env from './helper';
import AudioListener from './audio-listener';
import VideoFrameChecker from '../../webrtc.helpers/framechecker';

class VideoBlock {
    _audioContext = null;
    _audioLevel = 0;
    _audioLevelMapping = {
        0: '',
        1: 'voice-middle',
        2: 'voice-loud'
    };
    _audioListener = null;
    _frameChecker = null;
    _stream = null;
    _container = null;
    _options = {
        classList: ['video-wrapper'],
        autoplay: '',
        muted: false,
        state: 0,
        crossorigin: 'anonymous',
        playsinline: '',
        onBrokeStream: () => {}
    };
    _state = 0;
    _states = {
        'inited': 0,
        'connecting': 1,
        'waiting': 2,
        'playing': 3,
        'moving': 4,
        0: 'inited',
        1: 'connecting',
        2: 'waiting',
        3: 'playing',
        4: 'moving'
    };

    constructor(opt, stream = null, audioContext) {
        this._prepareOptions(opt);

        this._state = this._options.state;
        this._container = this._create();
        this._audioContext = audioContext;

        if (stream && audioContext) this.addStream(stream, audioContext);
    }

    _create() {
        let {classList, autoplay, crossorigin, state, playsinline} = this._options;
        let container = document.createElement('div');
        let spinner = document.createElement('div');
        let videoWrapper = document.createElement('div');
        let video = document.createElement('video');
        let audio = document.createElement('audio');
        // Live-tour WebRTC streams are not coordinated with tour media.
        video.dataset.media = 'exempt';
        audio.dataset.media = 'exempt';

        container.classList.add(...classList, this._states[state] || '');
        videoWrapper.classList.add('video-tag');
        spinner.classList.add('spinner');
        video.setAttribute('autoplay', autoplay);
        video.setAttribute('crossorigin', crossorigin);
        video.setAttribute('playsinline', playsinline);

        if (env.isiOS) {
            audio.setAttribute('autoplay', '');
            audio.setAttribute('playsinline', '');
            videoWrapper.appendChild(audio);
        }

        videoWrapper.appendChild(video);
        container.appendChild(videoWrapper);
        container.appendChild(spinner);

        return container;
    }

    _prepareOptions(opt) {
        Object.keys(opt).forEach((key) => {
            switch (key) {
                case 'classList':
                    this._options[key].push(...opt[key]);
                    break;
                default:
                    this._options[key] = opt[key];
            }
        })
    }

    _initAudioListener(stream) {
        console.log('Init audio listener');

        if (this._audioListener) return;

        this._audioListener = new AudioListener(stream, this._audioContext);
        this._displayAudioLevelBound = this._displayAudioLevel.bind(this);

        this._audioListener.on('audio.level', this._displayAudioLevelBound);
    }

    _destroyAudioListener() {
        if (!this._audioListener) return;

        this._audioListener.off('audio.level', this._displayAudioLevelBound);
        this._audioListener.destroy();

        delete this._displayAudioLevelBound;

        this._displayAudioLevel(0);
    }

    _displayAudioLevel(level) {
        if (this._audioLevelMapping[level] === undefined) return;

        if (this._audioLevelMapping[this._audioLevel])
            this._container.classList.remove(this._audioLevelMapping[this._audioLevel]);

        if (this._audioLevelMapping[level])
            this._container.classList.add(this._audioLevelMapping[level]);

        this._audioLevel = level;
    }

    _initStreamListeners(stream) {
        let video = this._container.querySelector('video');

        this._frameChecker = new VideoFrameChecker(video);
        this._frameChecker.on('e3-broken-stream', this._options.onBrokeStream);

        if (this._audioContext) {
            this._initAudioListenerBound = this._initAudioListener.bind(this, stream);
            video.addEventListener('canplay', this._initAudioListenerBound);
        }
    }

    _unsetStreamListeners() {
        let video = this._container.querySelector('video');

        if (this._frameChecker) {
            this._frameChecker.stop();
            this._frameChecker.off('e3-broken-stream', this._options.onBrokeStream);
        }

        if (this._audioContext) {
            this._destroyAudioListener();
            video.removeEventListener('canplay', this._initAudioListenerBound);

            delete this._initAudioListenerBound;
        }
    }

    get container() {
        return this._container;
    }

    get isPlaying() {
        return this._state === this._states['playing'];
    }

    addStream(stream, audioContext) {
        let {muted} = this._options;
        let video = this._container.querySelector('video');
        let audio = this._container.querySelector('audio') || null;

        video.srcObject = stream;
        video.muted = muted;

        // in case of iOS device which always has audio tag
        if (audio) {
            video.muted = true;
            audio.srcObject = stream;
            audio.muted = muted;
        }

        video.ondblclick = this._fullscreen.bind(this, video);
        video.removeAttribute('controls');

        this._stream = stream;

        if (!this._audioContext && audioContext)
            this._audioContext = audioContext;

        if(this._frameChecker){
            this._unsetStreamListeners();
        }

        this._initStreamListeners(stream);
    }

    _fullscreen(video) {
        if (video.requestFullscreen) {
            video.requestFullscreen();
        } else if (video.mozRequestFullScreen) {
            video.mozRequestFullScreen();
        } else if (video.webkitRequestFullscreen) {
            video.webkitRequestFullscreen();
        } else if (video.msRequestFullscreen) {
            video.msRequestFullscreen();
        }
    }

    removeStream() {
        let video = this._container.querySelector('video');
        let audio = this._container.querySelector('audio') || null;

        video.pause();
        video.srcObject = null;

        if (audio) {
            audio.pause();
            audio.srcObject = null;
        }

        this._stream = null;

        this._unsetStreamListeners();
    }

    setState(state) {
        if (!this._states[state]) return;

        if (typeof state === 'string') state = this._states[state];

        this._container.classList.remove(this._states[this._state]);
        this._container.classList.add(this._states[state]);
        this._state = state;
    }

    destroy() {
        this.removeStream();

        this._container.parentNode.removeChild(this._container);
    }
}

class RealtimeVideo {
    _dom = {};
    _streams = {};
    _audioContext = null;

    constructor(container, ownId, audioContext) {
        this._audioContext = audioContext;
        this._init(container, ownId);
    }

    _init(container, ownId) {
        this._dom = this._createWrapper();

        container.appendChild(this._dom.wrapper);

        this.add(ownId, {classList: ['video-tag-own'], muted: true}, 'own');
        this.add('empty', {classList: ['video-tag-agent'], state: 1}, 'agents');

    }

    _createWrapper() {
        let wrapper = document.createElement('div');
        let own = document.createElement('div');
        let agents = document.createElement('div');

        wrapper.setAttribute('id', 'realtime');
        wrapper.classList.add('realtime');

        own.setAttribute('id', 'own-video-wrapper');
        own.classList.add('own-video-wrapper');

        agents.setAttribute('id', 'connected-video-wrapper');
        agents.classList.add('connected-video-wrapper');

        wrapper.appendChild(own);
        wrapper.appendChild(agents);

        return {wrapper, own, agents};
    }

    getContainer() {
        const arr = [];
        for(const i in this._streams){
            arr.push({
                id: i,
                container: this._streams[i].container
            });
        }
        return arr;
    }

    updatesAllUserStates(state = 'playing'){
        const states = this.getStreamKeys();
        states.forEach((userID) => this.setState(userID, state))
    }

    getStreamKeys(){
         return Object.keys(this._streams);
    }

    add(id, opt, target = 'agents') {
        if (!this._dom[target]) return null;

        if (this._streams[id]) {
            this.remove(id);
            delete this._streams[id];
        }

        this._streams[id] = new VideoBlock(opt);
        this._dom[target].appendChild(this._streams[id].container);

        return this._streams[id];
    }

    addStream(id, stream) {
        if (!this._streams[id]) return;

        this._streams[id].addStream(stream, this._audioContext);
        // this._streams[id].setState(this.getState(id) || 'playing');
        this._streams[id].setState('playing');
    }

    addScreenStream(id, stream) {
        if (!this._streams[id]) return;

        this.remove(id);
        delete this._streams[id];

        this._streams[id] = new VideoBlock({classList: ['video-tag-agent'], muted: true});

        this._dom['own'].appendChild(this._streams[id].container);

        // this.removeStream();

        this._streams[id].addStream(stream, this._audioContext);
        this._streams[id].setState(this.getState(id) || 'playing');
        this._streams[id].setState('playing');
    }

    removeStream(id) {
        if (!this._streams[id]) return;

        this._streams[id].removeStream();
        this._streams[id].setState('inited');
    }

    getState(id) {
        return this._streams[id]._state;
    }

    setState(id, state) {
        if (!this._streams[id]) return;

        let videoBlock = this._streams[id];

        videoBlock.setState(state);
    }

    isPresent(id) {
        return !!this._streams[id];
    }

    isPlaying(id) {
        return this._streams[id].isPlaying;
    }

    remove(id) {
        if (!this._streams[id]) return;

        this._streams[id].destroy();
        delete this._streams[id];
    }
}

export {
    RealtimeVideo,
    VideoBlock
}

export default {
    RealtimeVideo,
    VideoBlock
};
